import React, { useState, useEffect } from 'react';
import { 
    ExclamationTriangleIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    CalendarIcon,
    ClockIcon,
    BuildingOfficeIcon,
    UserGroupIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface Building {
    _id: string;
    nombre: string;
    ciudad: string;
}

interface Property {
    _id: string;
    numero: string;
    tipo: string;
    edificio: Building;
}

interface Announcement {
    _id: string;
    titulo: string;
    contenido: string;
    nivelImportancia: 'baja' | 'media' | 'alta';
    fechaInicio: string;
    fechaFin: string;
    horaInicio: string;
    horaFin: string;
    edificios: Building[];
    propiedades: Property[];
    tiposUsuario: string[];
    creadoPor: {
        _id: string;
        nombre: string;
        apellido: string;
    };
    createdAt: string;
}

const Announcements: React.FC = () => {
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterImportance, setFilterImportance] = useState<string>('');

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/announcements/user'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setAnnouncements(data.data.announcements);
            } else {
                console.error('Error al obtener anuncios');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getImportanceIcon = (nivel: string) => {
        switch (nivel) {
            case 'alta':
                return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
            case 'media':
                return <InformationCircleIcon className="h-6 w-6 text-yellow-500" />;
            case 'baja':
                return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
            default:
                return <InformationCircleIcon className="h-6 w-6 text-gray-500" />;
        }
    };

    const getImportanceLabel = (nivel: string) => {
        const labels: { [key: string]: string } = {
            'alta': 'Alta',
            'media': 'Media',
            'baja': 'Baja'
        };
        return labels[nivel] || nivel;
    };

    const getImportanceColor = (nivel: string) => {
        switch (nivel) {
            case 'alta':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'media':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'baja':
                return 'bg-green-100 text-green-800 border-green-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getImportanceBorderColor = (nivel: string) => {
        switch (nivel) {
            case 'alta':
                return 'border-l-red-500';
            case 'media':
                return 'border-l-yellow-500';
            case 'baja':
                return 'border-l-green-500';
            default:
                return 'border-l-gray-500';
        }
    };

    const filteredAnnouncements = announcements.filter(announcement => {
        const matchesSearch = !searchTerm || 
            announcement.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            announcement.contenido.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesImportance = !filterImportance || announcement.nivelImportancia === filterImportance;
        
        return matchesSearch && matchesImportance;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                <h2 className="text-2xl font-semibold text-gray-600">Tablón de Anuncios</h2>
                <p className="text-gray-600 mt-2">Mantente informado sobre las últimas noticias y actualizaciones</p>
            </div>

            {/* Filtros y Búsqueda */}
            <div className="bg-white p-4 rounded-lg shadow-md border">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar anuncios..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div>
                        <select
                            value={filterImportance}
                            onChange={(e) => setFilterImportance(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los niveles</option>
                            <option value="alta">Alta importancia</option>
                            <option value="media">Media importancia</option>
                            <option value="baja">Baja importancia</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Lista de Anuncios */}
            {filteredAnnouncements.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md border p-8 text-center">
                    <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay anuncios</h3>
                    <p className="text-gray-600">
                        {searchTerm || filterImportance 
                            ? 'No se encontraron anuncios con los filtros aplicados.'
                            : 'No hay anuncios disponibles en este momento.'
                        }
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredAnnouncements.map((announcement) => (
                        <div 
                            key={announcement._id} 
                            className={`bg-white rounded-lg shadow-md border-l-4 ${getImportanceBorderColor(announcement.nivelImportancia)} border border-gray-200 p-6 hover:shadow-lg transition-shadow`}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    {getImportanceIcon(announcement.nivelImportancia)}
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {announcement.titulo}
                                    </h3>
                                </div>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getImportanceColor(announcement.nivelImportancia)}`}>
                                    {getImportanceLabel(announcement.nivelImportancia)}
                                </span>
                            </div>

                            <div className="prose max-w-none mb-4">
                                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                    {announcement.contenido}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-4">
                                <div className="flex items-center space-x-2">
                                    <CalendarIcon className="h-4 w-4" />
                                    <span>
                                        {new Date(announcement.fechaInicio).toLocaleDateString('es-ES', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })} {announcement.horaInicio} - {new Date(announcement.fechaFin).toLocaleDateString('es-ES', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })} {announcement.horaFin}
                                    </span>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                    <UserGroupIcon className="h-4 w-4" />
                                    <span>Para: {announcement.tiposUsuario.join(', ')}</span>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                    <BuildingOfficeIcon className="h-4 w-4" />
                                    <span>
                                        {announcement.edificios.length > 0 
                                            ? `${announcement.edificios.length} edificio(s)`
                                            : 'Todos los edificios'
                                        }
                                    </span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                                <div className="flex items-center space-x-2 text-sm text-gray-500">
                                    <ClockIcon className="h-4 w-4" />
                                    <span>
                                        Publicado el {new Date(announcement.createdAt).toLocaleDateString('es-ES', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-500">
                                    Por {announcement.creadoPor.nombre} {announcement.creadoPor.apellido}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Announcements;
